/**
 * Topbar — 60px premium bar: accent title rail + page title on the left;
 * theme toggle + the user avatar (mirrors the sidebar footer — gradient
 * avatar, presence dot, hover profile card) on the right.
 */
import React, { useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useSession } from '../context/sessionContext';
import { useAuthFlow } from '../navigation/authFlowContext';
import SkIcon from '../components/skIcon';

interface Props {
  title: string;
  // Kept for API compatibility with the shell; the inline "New" button was removed.
  onNewPress: () => void;
}

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});

/** Gradient avatar + presence dot, with a hover profile card dropping below —
 *  the same treatment as the sidebar footer, placed at the top-right. */
const TopUserMenu: React.FC = () => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const { exitApp } = useAuthFlow();
  const { user, role } = useSession();

  const titleCase = (s: string) =>
    s
      .split(/\s+/)
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
      .join(' ');
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || '–';
  const fullName = user ? titleCase(`${user.firstName} ${user.lastName}`) : 'Signed in';
  const email = user?.email || 'Signed in';
  const roleLabel = role ? titleCase(role) : '';

  const [hover, setHover] = useState(false);
  const [logoutHover, setLogoutHover] = useState(false);

  const avatar = (size: number) => (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#db2777',
        ...web({ backgroundImage: 'linear-gradient(135deg, #f97316, #db2777)' }),
      }}
    >
      <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: size * 0.38, color: '#fff' }}>{initials}</Text>
    </View>
  );

  return (
    <View
      style={{ position: 'relative' }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      {/* Name + avatar pill */}
      <Pressable
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          height: 42,
          paddingLeft: 15,
          paddingRight: 6,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: hover ? colors.lineStrong : colors.line,
          backgroundColor: hover ? colors.paper2 : colors.card,
          ...theme.shadow(hover ? 'base' : 'sm'),
          ...web({ transition: 'background-color .15s ease, border-color .15s ease, box-shadow .15s ease', cursor: 'pointer' }),
        }}
      >
        <Text
          numberOfLines={1}
          style={{ maxWidth: 190, fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink, letterSpacing: 0.4, textTransform: 'uppercase' }}
        >
          {fullName}
        </Text>
        <View style={{ width: 30, height: 30 }}>
          <View
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#db2777',
              ...web({
                backgroundImage: 'linear-gradient(135deg, #f97316, #db2777)',
                boxShadow: hover ? '0 6px 14px -3px rgba(219,39,119,0.5)' : '0 3px 8px -2px rgba(219,39,119,0.45)',
                transition: 'box-shadow .15s ease',
              }),
            }}
          >
            <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 12, color: '#fff' }}>{initials}</Text>
          </View>
          {/* presence dot */}
          <View
            style={{
              position: 'absolute',
              right: -1,
              bottom: -1,
              width: 10,
              height: 10,
              borderRadius: 5,
              backgroundColor: colors.success,
              borderWidth: 2,
              borderColor: colors.card,
            }}
          />
        </View>
      </Pressable>

      {/* Hover profile card — drops below the avatar */}
      {isWeb && (
        <View
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            paddingTop: 10, // transparent bridge so the cursor can reach the card
            ...web({
              opacity: hover ? 1 : 0,
              visibility: hover ? 'visible' : 'hidden',
              transform: hover ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.98)',
              transformOrigin: 'top right',
              transition: 'opacity .15s ease, transform .15s ease, visibility .15s',
              pointerEvents: hover ? 'auto' : 'none',
            }),
          }}
        >
          {/* caret pointing up to the avatar */}
          <View
            style={{
              position: 'absolute',
              top: 5,
              right: 13,
              width: 12,
              height: 12,
              backgroundColor: colors.card,
              borderTopWidth: 1,
              borderLeftWidth: 1,
              borderColor: colors.line,
              ...web({ transform: 'rotate(45deg)' }),
            }}
          />
          <View
            style={{
              width: 300,
              padding: 6,
              backgroundColor: colors.card,
              borderWidth: 1,
              borderColor: colors.line,
              borderRadius: 14,
              ...theme.shadow('lg'),
            }}
          >
            {/* Identity header — tinted, with a role badge */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                padding: 10,
                borderRadius: 10,
                backgroundColor: colors.paper2,
              }}
            >
              <View
                style={{
                  ...web({ boxShadow: `0 0 0 2px ${colors.card}, 0 3px 8px -2px rgba(219,39,119,0.45)`, borderRadius: 20 }),
                }}
              >
                {avatar(40)}
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text numberOfLines={1} style={{ fontFamily: font.sansSemibold, fontWeight: '600', fontSize: 13.5, color: colors.ink, letterSpacing: -0.1 }}>
                  {fullName}
                </Text>
                <Text numberOfLines={2} style={{ fontFamily: font.sansMedium, fontWeight: '500', fontSize: 11.5, color: colors.ink3, marginTop: 2, ...web({ wordBreak: 'break-all' }) }}>
                  {email}
                </Text>
                {!!roleLabel && (
                  <View style={{ flexDirection: 'row', marginTop: 6 }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 5,
                        paddingVertical: 2,
                        paddingHorizontal: 8,
                        borderRadius: 999,
                        backgroundColor: colors.accentSoft,
                        borderWidth: 1,
                        borderColor: colors.accentLine,
                      }}
                    >
                      <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.accent }} />
                      <Text style={{ fontFamily: font.sansSemibold, fontWeight: '600', fontSize: 10, color: colors.accentInk, letterSpacing: 0.2 }}>
                        {roleLabel}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </View>

            <View style={{ height: 1, marginVertical: 6, marginHorizontal: 6, backgroundColor: colors.line }} />

            <Pressable
              onPress={exitApp}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                paddingVertical: 10,
                paddingHorizontal: 10,
                borderRadius: 9,
                backgroundColor: logoutHover ? colors.dangerSoft : 'transparent',
                ...web({ transition: 'background-color .1s ease', cursor: 'pointer' }),
              }}
              {...(isWeb ? { onMouseEnter: () => setLogoutHover(true), onMouseLeave: () => setLogoutHover(false) } : {})}
            >
              <SkIcon name="logout" size={15} color={colors.danger} strokeWidth={2} />
              <Text style={{ flex: 1, fontFamily: font.sansMedium, fontWeight: '500', fontSize: 13, color: colors.danger }}>Log out</Text>
              <SkIcon name="chevRight" size={14} color={logoutHover ? colors.danger : colors.dangerLine} strokeWidth={2} />
            </Pressable>
          </View>
        </View>
      )}
    </View>
  );
};

const Topbar: React.FC<Props> = ({ title }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;

  return (
    <View
      style={{
        height: 60,
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        paddingHorizontal: 24,
        borderBottomWidth: 1,
        borderBottomColor: colors.line,
        backgroundColor: colors.card,
        zIndex: 20,
        ...theme.shadow('sm'),
        ...web({ backgroundImage: `linear-gradient(180deg, ${colors.card}, ${colors.paper})` }),
      }}
    >
      {/* Title with accent rail */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
        <View
          style={{
            width: 3,
            height: 20,
            borderRadius: 3,
            backgroundColor: colors.accent,
            ...web({ backgroundImage: `linear-gradient(180deg, ${colors.accent}, ${colors.accentInk})` }),
          }}
        />
        <Text
          numberOfLines={1}
          style={{ fontFamily: font.sansBold, fontSize: 19, fontWeight: '700', color: colors.ink, letterSpacing: -0.4 }}
        >
          {title}
        </Text>
      </View>

      {/* User name + avatar pill */}
      <TopUserMenu />
    </View>
  );
};

export default Topbar;
